import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain, Check, X, Sparkles } from "lucide-react";
import { api, apiError, num } from "@/lib/api";
import { Btn, Tag } from "@/components/Shell";

/** "This one is low — what do you swap for?" The engine learns from every answer. */
export const SwapPrompt = ({ productId, sku, shortfall, onLearned, dismissible = false, onDismiss }) => {
  const [candidates, setCandidates] = useState(null);
  const [selected, setSelected] = useState("");
  const [ratio, setRatio] = useState("1");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/substitutions/suggestions/${productId}`);
      setCandidates(data);
      if (data.length) {
        setSelected(data[0].product_id);
        setRatio(String(data[0].ratio ?? 1));
      }
    } catch (e) {
      setCandidates([]);
      if (e?.response?.status !== 404) toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const decide = async (accepted) => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/substitutions/decide", {
        product_id: productId,
        alternative_id: selected,
        accepted,
        ratio: accepted ? Number(ratio) || 1 : null,
        quantity: shortfall || null,
      });
      toast.success(
        accepted
          ? data.auto_applied
            ? `Learned — ${Math.round(data.confidence * 100)}% confidence, now applied automatically`
            : `Noted (${data.accepted} time${data.accepted === 1 ? "" : "s"} chosen)${data.learned_ratio ? ` · learned ratio ${data.learned_ratio}` : ""}`
          : "Noted — we'll rank that one lower next time"
      );
      await load();
      onLearned?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const chosen = candidates?.find((c) => c.product_id === selected);

  return (
    <div className="border border-amber-200 bg-amber-50/60 p-3" data-testid={`swap-prompt-${sku}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-medium text-amber-900">
          <Brain className="h-4 w-4" />
          {sku} is low{shortfall ? ` by ${num(shortfall)} units` : ""} — what do you swap for?
        </p>
        {dismissible && (
          <button
            data-testid={`swap-prompt-dismiss-${sku}`}
            onClick={onDismiss}
            className="text-amber-700 transition-colors hover:text-amber-900"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {candidates === null ? (
        <p className="mt-2 text-xs text-amber-800">Loading suggestions…</p>
      ) : candidates.length === 0 ? (
        <p className="mt-2 text-xs text-amber-800">No comparable products to suggest yet.</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-amber-900">
            Swap for
            <select
              data-testid={`swap-select-${sku}`}
              value={selected}
              onChange={(e) => {
                setSelected(e.target.value);
                const c = candidates.find((x) => x.product_id === e.target.value);
                setRatio(String(c?.ratio ?? 1));
              }}
              className="mono mt-1 block w-64 border border-amber-300 bg-white px-2 py-2 text-xs focus:border-slate-900 focus:outline-none"
            >
              {candidates.map((c) => (
                <option key={c.product_id} value={c.product_id}>
                  {c.sku} · {num(c.available)} avail
                  {c.accepted ? ` · chosen ${c.accepted}×` : ""}
                  {c.confidence ? ` · ${Math.round(c.confidence * 100)}%` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-amber-900">
            Ratio
            <input
              data-testid={`swap-ratio-${sku}`}
              value={ratio}
              onChange={(e) => setRatio(e.target.value)}
              className="mono mt-1 block w-20 border border-amber-300 px-2 py-2 text-xs focus:border-slate-900 focus:outline-none"
            />
          </label>
          <Btn data-testid={`swap-confirm-${sku}`} disabled={busy} onClick={() => decide(true)}>
            <Check className="h-4 w-4" /> Use this
          </Btn>
          <Btn variant="outline" data-testid={`swap-reject-${sku}`} disabled={busy} onClick={() => decide(false)}>
            Not this one
          </Btn>
          {chosen?.auto_applied && (
            <Tag tone="violet" testId={`swap-auto-${sku}`}>
              <Sparkles className="mr-1 inline h-3 w-3" />Auto-applied
            </Tag>
          )}
          {chosen?.learned_ratio && (
            <span className="text-[11px] text-amber-800">Learned ratio {chosen.learned_ratio}</span>
          )}
        </div>
      )}
      <p className="mt-2 text-[11px] text-amber-800">
        Every answer trains the engine — pairs chosen 3+ times become the top suggestion, and 90%+
        confidence pairs are applied automatically on the gap dashboard.
      </p>
    </div>
  );
};
